import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AppDispatch } from '../../../../../app/store';
import {
  setScheduleColumnWidth,
  setScheduleItemScaleMultiplier,
} from '../../../../../features/schedule/scheduleSlice';
import {
  clampScheduleColumnWidthWithFloors,
  clampScheduleScaleWithFloors,
  SCHEDULE_ZOOM_SCALE_STEP,
  type ScheduleZoomFloors,
} from '../../../../../features/schedule/scheduleZoomBounds';
import { SCHEDULE_ZOOM_COLUMN_WIDTH_STEP } from './scheduleLayoutHooks';
import { applyLiveScheduleZoomCss } from './scheduleZoomCanvasRegistry';

const ZOOM_COMMIT_IDLE_MS = 150;

export type ScheduleZoomAnchorCapture = (
  clientX: number,
  clientY: number,
  nextScale: number,
  nextWidth: number,
  oldScale: number,
  oldWidth: number
) => void;

type UseScheduleZoomGestureParams = {
  reduxScale: number;
  reduxColumnWidth: number;
  scheduleZoomFloors: ScheduleZoomFloors;
  dispatch: AppDispatch;
  captureZoomAnchor: ScheduleZoomAnchorCapture;
};

export function useScheduleZoomGesture({
  reduxScale,
  reduxColumnWidth,
  scheduleZoomFloors,
  dispatch,
  captureZoomAnchor,
}: UseScheduleZoomGestureParams) {
  const [liveScale, setLiveScale] = useState(reduxScale);
  const [liveColumnWidth, setLiveColumnWidth] = useState(reduxColumnWidth);
  const [isGesturing, setIsGesturing] = useState(false);

  const liveScaleRef = useRef(reduxScale);
  const liveColumnWidthRef = useRef(reduxColumnWidth);
  const isGesturingRef = useRef(false);
  const pendingWheelRef = useRef<{
    zoomIn: boolean;
    clientX: number;
    clientY: number;
  } | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isGesturingRef.current) return;
    liveScaleRef.current = reduxScale;
    liveColumnWidthRef.current = reduxColumnWidth;
    setLiveScale(reduxScale);
    setLiveColumnWidth(reduxColumnWidth);
  }, [reduxScale, reduxColumnWidth]);

  const scheduleCommit = useCallback(() => {
    if (commitTimerRef.current != null) {
      clearTimeout(commitTimerRef.current);
    }
    commitTimerRef.current = setTimeout(() => {
      commitTimerRef.current = null;
      isGesturingRef.current = false;
      const scale = liveScaleRef.current;
      const width = liveColumnWidthRef.current;
      setLiveScale(scale);
      setLiveColumnWidth(width);
      setIsGesturing(false);
      dispatch(setScheduleItemScaleMultiplier(scale));
      dispatch(setScheduleColumnWidth(width));
    }, ZOOM_COMMIT_IDLE_MS);
  }, [dispatch]);

  const applyZoomStep = useCallback(
    (zoomIn: boolean, anchor?: { clientX: number; clientY: number }) => {
      const oldScale = liveScaleRef.current;
      const oldWidth = liveColumnWidthRef.current;
      const nextScale = clampScheduleScaleWithFloors(
        oldScale + (zoomIn ? SCHEDULE_ZOOM_SCALE_STEP : -SCHEDULE_ZOOM_SCALE_STEP),
        scheduleZoomFloors,
        { bumpToViewportFill: true }
      );
      const nextWidth = clampScheduleColumnWidthWithFloors(
        oldWidth + (zoomIn ? SCHEDULE_ZOOM_COLUMN_WIDTH_STEP : -SCHEDULE_ZOOM_COLUMN_WIDTH_STEP),
        scheduleZoomFloors,
        { bumpToViewportFill: true }
      );
      if (nextScale === oldScale && nextWidth === oldWidth) {
        return;
      }
      const wasGesturing = isGesturingRef.current;
      isGesturingRef.current = true;
      if (!wasGesturing) {
        setIsGesturing(true);
      }
      liveScaleRef.current = nextScale;
      liveColumnWidthRef.current = nextWidth;
      applyLiveScheduleZoomCss(nextScale, nextWidth);
      if (anchor) {
        captureZoomAnchor(
          anchor.clientX,
          anchor.clientY,
          nextScale,
          nextWidth,
          oldScale,
          oldWidth
        );
      }
      scheduleCommit();
    },
    [scheduleZoomFloors, captureZoomAnchor, scheduleCommit]
  );

  const flushWheelFrame = useCallback(() => {
    rafIdRef.current = null;
    const pending = pendingWheelRef.current;
    if (!pending) return;
    pendingWheelRef.current = null;
    applyZoomStep(pending.zoomIn, { clientX: pending.clientX, clientY: pending.clientY });
  }, [applyZoomStep]);

  const queueWheelZoom = useCallback(
    (zoomIn: boolean, clientX: number, clientY: number) => {
      pendingWheelRef.current = { zoomIn, clientX, clientY };
      if (rafIdRef.current == null) {
        rafIdRef.current = requestAnimationFrame(flushWheelFrame);
      }
    },
    [flushWheelFrame]
  );

  useEffect(() => {
    return () => {
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (commitTimerRef.current != null) {
        clearTimeout(commitTimerRef.current);
      }
      if (isGesturingRef.current) {
        isGesturingRef.current = false;
        setIsGesturing(false);
        dispatch(setScheduleItemScaleMultiplier(liveScaleRef.current));
        dispatch(setScheduleColumnWidth(liveColumnWidthRef.current));
      }
    };
  }, [dispatch]);

  return {
    scale: liveScale,
    columnWidth: liveColumnWidth,
    isGesturing,
    applyZoomStep,
    queueWheelZoom,
  };
}
